import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { AuthManager } from '@/contexts/AuthManager';

interface IdleWarningDetail {
  timeLeftMs: number;
  resolve: (stay: boolean) => void;
  reason?: 'idle' | 'token-expiration'; // NEW
}

// Formats a ms duration as mm:ss
function formatTime(ms: number) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const mm = m.toString().padStart(1, '0');
  const ss = s.toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

const IdleTimeoutDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [timeLeftMs, setTimeLeftMs] = useState<number>(0);
  const [reason, setReason] = useState<'idle' | 'token-expiration'>('idle');
  const resolverRef = useRef<((stay: boolean) => void) | null>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    // Single handler for both event types
    const handleWarning = (e: Event) => {
      const ce = e as CustomEvent<IdleWarningDetail>;
      try {
        ce.preventDefault?.();
      } catch {
        // Intentionally ignored - preventDefault may not be available
      }
      const detail = ce.detail;
      if (!detail) return;

      // If a dialog is already open, update the remaining time and replace resolver
      setTimeLeftMs(detail.timeLeftMs);
      setReason(detail.reason || 'idle'); // Default to idle for backward compat
      resolverRef.current = detail.resolve;
      setOpen(true);

      // Start/refresh the countdown
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
      intervalRef.current = window.setInterval(() => {
        setTimeLeftMs((prev) => Math.max(0, prev - 1000));
      }, 1000);
    };

    // Listen to BOTH event types with same handler
    window.addEventListener('arterra:idle-warning', handleWarning as EventListener);
    window.addEventListener('arterra:token-warning', handleWarning as EventListener);

    return () => {
      window.removeEventListener('arterra:idle-warning', handleWarning as EventListener);
      window.removeEventListener('arterra:token-warning', handleWarning as EventListener);
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const closeAndResolve = (stay: boolean) => {
    // Resolve back to AuthManager
    const resolver = resolverRef.current;
    resolverRef.current = null;
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setOpen(false);
    if (resolver) {
      try {
        resolver(stay);
      } catch {
        // Intentionally ignored - resolver errors are non-critical
      }
    }
  };

  const onStaySignedIn = async () => {
    // Let AuthManager handle refresh and activity bump after it receives true
    closeAndResolve(true);
  };

  const onLogoutNow = async () => {
    // Perform immediate logout for better UX
    try {
      closeAndResolve(false);
      await AuthManager.getInstance().performLogout();
    } catch {
      // Intentionally ignored - logout will be attempted anyway
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // If the user dismisses the dialog without choosing, treat as "not staying"
        if (!v && open && resolverRef.current) {
          closeAndResolve(false);
        } else {
          setOpen(v);
        }
      }}
    >
      <DialogContent className="bg-white text-neutral-900 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {reason === 'token-expiration'
              ? 'Session Expiring Soon'
              : 'Are you still there?'
            }
          </DialogTitle>
          <DialogDescription className="text-neutral-600">
            {reason === 'token-expiration'
              ? 'Your authentication token will expire soon. Would you like to refresh your session?'
              : 'Your session will automatically sign out soon due to inactivity.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className={`mt-2 rounded-md border p-3 ${
          reason === 'token-expiration'
            ? 'bg-blue-50 border-blue-200'
            : 'bg-amber-50 border-amber-200'
        }`}>
          <div className="text-sm">Time remaining</div>
          <div className={`text-2xl font-bold tabular-nums ${
            reason === 'token-expiration'
              ? 'text-blue-600'
              : 'text-amber-800'
          }`}>
            {formatTime(timeLeftMs)}
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="secondary" onClick={onLogoutNow} className="min-w-[120px]">
            {reason === 'token-expiration' ? 'Logout' : 'Log out now'}
          </Button>
          <Button onClick={onStaySignedIn} className="min-w-[140px]">
            {reason === 'token-expiration'
              ? 'Refresh Session'
              : 'Stay signed in'
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IdleTimeoutDialog;
